import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  IconLayoutDashboard as DashboardIcon,
  IconBuildingBank as BudgetIcon,
  IconWallet as LedgerIcon
} from '@tabler/icons-react';
import api from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { getCachedList, setCachedList, LEDGERS_LIST_CACHE_KEY, BUDGETS_LIST_CACHE_KEY } from '../utils/listCacheUtil';
import { balancesFromTangible } from '../utils/tangibleBalances';
import { byName, groupBudgetsByCategory } from '../utils/budgetGroups';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { isNavIdleRoute } from '../utils/detailRoute';
import { ACTION_BAR_HEIGHT_VAR } from './ds/ActionBar';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeGroupId } = useGroup();
  const strings = useEngineStrings();
  const [openDrawer, setOpenDrawer] = useState(null); // 'ledgers' | 'budgets' | null
  const [anchorX, setAnchorX] = useState(null);
  const [ledgers, setLedgers] = useState(null);
  const [budgets, setBudgets] = useState(null);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState({});
  const closeTimerRef = useRef(null);
  const ledgersRef = useRef(null);
  const budgetsRef = useRef(null);
  const drawerRef = useRef(null);

  // #2158: one /tangible read carries every live balance (active-only since
  // #2156); each drawer merges its own kind, in dollars, into the shared map.
  // It runs on the cached-list path too, which used to show no balances.
  const fetchBalances = useCallback((kind) => {
    if (!activeGroupId) return;
    api.get(activeGroupId, '/tangible')
      .then((t) => {
        const cents = balancesFromTangible(t)[kind];
        const map = {};
        Object.keys(cents).forEach((id) => { map[id] = cents[id] / 100; });
        setBalances(prev => ({ ...prev, ...map }));
      })
      .catch((err) => console.warn('Nav: failed to load balances', err));
  }, [activeGroupId]);

  const fetchLedgers = useCallback(async () => {
    // Check shared cache first
    const cached = getCachedList(LEDGERS_LIST_CACHE_KEY);
    if (cached) {
      setLedgers(cached);
      fetchBalances('ledgers');
      return;
    }
    if (!activeGroupId) return;
    setLoading(prev => ({ ...prev, ledgers: true }));
    try {
      const response = await api.get(activeGroupId, '/ledgers');
      const data = (response.ledgers || []).filter(l => !l.archived);
      setLedgers(data);
      setCachedList(LEDGERS_LIST_CACHE_KEY, data);
      fetchBalances('ledgers');
    } catch (err) {
      console.warn('Nav: failed to load ledgers', err);
      setLedgers([]);
    } finally {
      setLoading(prev => ({ ...prev, ledgers: false }));
    }
  }, [activeGroupId, fetchBalances]);

  const fetchBudgets = useCallback(async () => {
    // #2116: the cache holds the whole /budgets body, shared with the ledger
    // budget picker; the categories group this drawer.
    const cached = getCachedList(BUDGETS_LIST_CACHE_KEY);
    if (cached?.budgets) {
      setBudgets(cached.budgets.filter(b => b.budgetId !== '0'));
      setBudgetCategories(cached.budgetCategories || []);
      fetchBalances('budgets');
      return;
    }
    if (!activeGroupId) return;
    setLoading(prev => ({ ...prev, budgets: true }));
    try {
      const response = await api.get(activeGroupId, '/budgets');
      const data = (response.budgets || []).filter(b => b.budgetId !== '0');
      const categories = response.budgetCategories || [];
      setBudgets(data);
      setBudgetCategories(categories);
      setCachedList(BUDGETS_LIST_CACHE_KEY, { budgets: data, budgetCategories: categories });
      fetchBalances('budgets');
    } catch (err) {
      console.warn('Nav: failed to load budgets', err);
      setBudgets([]);
    } finally {
      setLoading(prev => ({ ...prev, budgets: false }));
    }
  }, [activeGroupId, fetchBalances]);

  const handleOpen = useCallback((type) => {
    clearTimeout(closeTimerRef.current);
    const ref = type === 'ledgers' ? ledgersRef : budgetsRef;
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setAnchorX(rect.left + rect.width / 2);
    }
    setOpenDrawer(type);
    if (type === 'ledgers' && ledgers === null) fetchLedgers();
    if (type === 'budgets' && budgets === null) fetchBudgets();
  }, [ledgers, budgets, fetchLedgers, fetchBudgets]);

  const handleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpenDrawer(null);
    }, 150);
  }, []);

  const handleKeepOpen = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    return () => clearTimeout(closeTimerRef.current);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setOpenDrawer(null);
  }, [location.pathname]);

  // Reset the drawer lists on a workspace switch. They live in component state
  // and refetch only while null, and <Navigation /> never remounts, so without
  // this the previous workspace's ledgers persist indefinitely (#1780). The
  // localStorage list caches are already flushed by setActiveGroupId (#855).
  useEffect(() => {
    setLedgers(null);
    setBudgets(null);
    setBudgetCategories([]);
    setBalances({});
  }, [activeGroupId]);

  const getValueFromPath = (pathname) => {
    if (pathname === '/') return 0;
    if (pathname.startsWith('/ledgers')) return 1;
    if (pathname.startsWith('/budgets')) return 2;
    return 0;
  };

  const handleChange = (event, newValue) => {
    const paths = ['/', '/ledgers', '/budgets'];
    navigate(paths[newValue]);
  };

  const items = openDrawer === 'ledgers' ? ledgers : openDrawer === 'budgets' ? budgets : null;
  const isLoading = openDrawer ? loading[openDrawer] : false;
  const basePath = openDrawer === 'ledgers' ? '/ledgers' : '/budgets';

  // #2116: once the workspace has a category the budgets drawer groups its
  // rows under headers, in the Budgets page order; without one it stays flat.
  const budgetGroups = openDrawer === 'budgets' && budgets && budgetCategories.length > 0
    ? [
        ...groupBudgetsByCategory(budgets.filter((b) => b.type === 0), budgetCategories, strings['budgetCategories.default']),
        { key: 'pending-income', name: 'Pending Income', members: budgets.filter((b) => b.type === 3).sort(byName) },
        { key: 'planned-expenses', name: 'Planned Expenses', members: budgets.filter((b) => b.type === 2).sort(byName) },
      ].filter((group) => group.members.length > 0)
    : null;

  const renderItem = (item) => {
    const itemId = openDrawer === 'ledgers' ? item.ledgerId : item.budgetId;
    const rawBal = balances[itemId];
    const hasBalance = typeof rawBal === 'number';
    // Pending Income budgets (type=3): flip sign on display so a raw
    // negative balance reads as the positive amount, matching the
    // header /tangible total and budget-detail (CLAUDE.md budget types).
    const bal = hasBalance && openDrawer === 'budgets' && item.type === 3 ? -rawBal : rawBal;
    const isNeg = hasBalance && bal < 0;
    return (
      <ListItemButton
        key={itemId}
        component={Link}
        to={`${basePath}/${itemId}`}
        sx={{
          py: 1,
          px: 2.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{ variant: 'body2' }}
        />
        {hasBalance && (
          <Typography
            variant="caption"
            color={isNeg ? 'error' : 'text.secondary'}
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          >
            {isNeg ? `($${Math.abs(bal).toFixed(2)})` : `$${bal.toFixed(2)}`}
          </Typography>
        )}
      </ListItemButton>
    );
  };

  // V4_ADDENDUM convention 8: on detail pages the section's pill indicator
  // stays, but the icon + label render idle - full active color is reserved
  // for the list pages themselves.
  const onDetail = isNavIdleRoute(location.pathname);

  // Bottom-nav item styling (DESIGN_SYSTEM.md section 7.3, v4 board): inactive
  // icon + label are faint; the active item turns interactive and grows a
  // 14x2px pill above the icon, scaling in horizontally per section 6.7. The
  // animation is suppressed under prefers-reduced-motion (section 6.2).
  const navActionSx = (theme) => ({
    position: 'relative',
    flex: '0 0 auto',
    minWidth: 'auto',
    maxWidth: 'none',
    color: theme.palette.bar.navIdle,
    '& svg': { width: theme.ds.size.navIcon, height: theme.ds.size.navIcon },
    '& .MuiBottomNavigationAction-label': {
      fontSize: '11px',
      marginTop: '3px',
    },
    '& .MuiBottomNavigationAction-label.Mui-selected': {
      fontSize: '11px',
    },
    '&.Mui-selected': {
      // Convention 8: idle color on detail pages, active on the list page.
      color: onDetail ? theme.palette.bar.navIdle : theme.palette.bar.navActive,
    },
    '&.Mui-selected::before': {
      content: '""',
      position: 'absolute',
      top: '6px',
      left: '50%',
      width: `${theme.ds.size.navPillW}px`,
      height: `${theme.ds.size.navPillH}px`,
      borderRadius: '1px',
      // The pill itself stays active-colored even on detail pages.
      backgroundColor: theme.palette.bar.navActive,
      transform: 'translateX(-50%)',
      animation: `nav-pill-in ${theme.ds.motion.base} ${theme.ds.motion.easeOut} backwards`,
    },
    '@keyframes nav-pill-in': {
      from: { opacity: 0, transform: 'translateX(-50%) scaleX(0.4)' },
      to: { opacity: 1, transform: 'translateX(-50%) scaleX(1)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&.Mui-selected::before': { animation: 'none' },
    },
  });

  return (
    <>
      {/* Slide-up drawer */}
      <Box
        ref={drawerRef}
        onMouseEnter={handleKeepOpen}
        onMouseLeave={handleClose}
        sx={{
          position: 'fixed',
          // #1750: seat the panel above whatever bar sits below it. The 56px is
          // the fixed bottom nav; the variable is published by ds/ActionBar
          // while one is mounted and removed when it unmounts, so pages without
          // an action bar resolve to the plain 56px they always used.
          bottom: `calc(56px + var(${ACTION_BAR_HEIGHT_VAR}, 0px))`,
          left: anchorX ?? '50%',
          zIndex: 999,
          transform: openDrawer ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(100%)',
          opacity: openDrawer ? 1 : 0,
          transition: 'transform 200ms ease-out, opacity 150ms ease-out',
          pointerEvents: openDrawer ? 'auto' : 'none',
        }}
      >
        <Paper
          // §2.7 overlay surface: this Paper is a custom slide-up (not a
          // MuiDrawer), so the global MuiMenu/Popover/Autocomplete overrides
          // do not catch it. Read overlay tokens directly so dark + light both
          // separate cleanly from the page.
          elevation={0}
          sx={(theme) => ({
            width: 'max-content',
            borderRadius: `${theme.ds.radius.overlay}px ${theme.ds.radius.overlay}px 0 0`,
            overflow: 'hidden',
            backgroundColor: theme.palette.surface.overlay,
            border: `1px solid ${theme.palette.overlay.border}`,
            borderBottom: 'none',
            boxShadow: theme.palette.overlay.shadow,
          })}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : items && items.length > 0 ? (
            <List
              dense
              disablePadding
              // #1750: 60vh stays the normal cap, but the panel can no longer
              // be taller than the room left between its own seat and the top
              // of the viewport, or the last rows scroll to a place the finger
              // cannot reach. 16px keeps it off the very top edge; dvh tracks
              // mobile browser chrome as it collapses, which vh does not.
              sx={{
                maxHeight: `min(60vh, calc(100dvh - 56px - var(${ACTION_BAR_HEIGHT_VAR}, 0px) - 16px))`,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {budgetGroups
                ? budgetGroups.flatMap((group) => [
                    // In-list group header, the payee picker's Ledgers header
                    // style (DESIGN_SYSTEM.md 8.15): 11px, uppercase, dim, tracked.
                    <Box
                      component="li"
                      key={`group-${group.key}`}
                      data-nav-budget-group={group.key}
                      sx={{
                        padding: '8px 20px 2px',
                        fontSize: '11px',
                        fontWeight: 500,
                        letterSpacing: '.06em',
                        textTransform: 'uppercase',
                        color: 'text.dim',
                      }}
                    >
                      {group.name}
                    </Box>,
                    ...group.members.map(renderItem),
                  ])
                : items.map(renderItem)}
            </List>
          ) : (
            <Box sx={{ py: 2, px: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                No {openDrawer} found
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Bottom navigation bar */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: theme.palette.surface.bar,
          borderTop: `1px solid ${theme.palette.divider}`,
        })}
      >
        <BottomNavigation
          value={getValueFromPath(location.pathname)}
          onChange={handleChange}
          showLabels
          // v4 board: desktop items centered with a 96px gap; narrower
          // viewports spread evenly.
          sx={{
            backgroundColor: 'transparent',
            justifyContent: { xs: 'space-around', md: 'center' },
            gap: { xs: 0, md: '96px' },
          }}
        >
          <BottomNavigationAction data-tour-id="nav-dashboard" label="Dashboard" icon={<DashboardIcon />} sx={navActionSx} />
          <BottomNavigationAction
            ref={ledgersRef}
            data-tour-id="nav-ledgers"
            label="Ledgers"
            icon={<LedgerIcon />}
            onMouseEnter={() => handleOpen('ledgers')}
            onMouseLeave={handleClose}
            sx={navActionSx}
          />
          <BottomNavigationAction
            ref={budgetsRef}
            data-tour-id="nav-budgets"
            label="Budgets"
            icon={<BudgetIcon />}
            onMouseEnter={() => handleOpen('budgets')}
            onMouseLeave={handleClose}
            sx={navActionSx}
          />
        </BottomNavigation>
      </Paper>
    </>
  );
};

export default Navigation;
